<script lang="ts">
import { XIcon } from 'lucide-vue-next';
import AppSelect from '@/components/v2/AppSelect.vue';

export default {
  name: 'OptionSearch',
  components: { XIcon, AppSelect },
};
</script>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref, watch } from 'vue';

interface Option {
  value: string;
  label: string;
}

const props = withDefaults(
  defineProps<{
    options: Option[];
    query: string;
    selectedOption: string;
    debounce?: number;
  }>(),
  {
    query: '',
    selectedOption: '',
    debounce: 750,
  },
);

const emit = defineEmits(['update:query', 'update:selected-option']);

const queryInput = ref(props.query);
const searchTimeout = ref<NodeJS.Timeout>();
const searchInput = ref<HTMLInputElement | null>(null);
const localSelectedOption = ref(props.selectedOption);

watch(
  () => props.selectedOption,
  (newVal) => {
    localSelectedOption.value = newVal;
  }
);

watch(
  () => props.query,
  (newVal) => {
    queryInput.value = newVal;
  },
);

watch(queryInput, (newVal, oldVal) => {
  if (searchTimeout.value) {
    clearTimeout(searchTimeout.value);
  }
  if (oldVal.trim() === newVal.trim()) {
    return;
  }
  searchTimeout.value = setTimeout(() => {
    emit('update:query', newVal.trim());
  }, props.debounce);
});

// Methods
function handleClear() {
  queryInput.value = '';
  emit('update:query', '');
}

function updateOption(value: string) {
  emit('update:selected-option', value);
}

function keyboardEventHandler(e: KeyboardEvent) {
  if (e.key === 'Enter' && document.activeElement === searchInput.value) {
    if (searchTimeout.value) {
      clearTimeout(searchTimeout.value);
    }

    emit('update:query', queryInput.value.trim());
  }
}

// Lifecycle
onMounted(() => {
  document.addEventListener('keydown', keyboardEventHandler);
});

onBeforeUnmount(() => {
  document.removeEventListener('keydown', keyboardEventHandler);
});
</script>

<template>
  <div class="tw:relative tw:flex tw:items-center tw:w-full tw:h-10 tw:bg-white tw:rounded-md tw:border tw:border-gray-300" data-testid="select-search">
    <div class="tw:flex tw:items-center tw:h-full tw:border-r tw:border-gray-300">
      <AppSelect
        data-testid="select-search-field"
        :modelValue="localSelectedOption"
        :options="options"
        class="tw:h-full tw:px-3"
        menuClass="tw:w-[150px]"
        noBorder
        @update:modelValue="updateOption"
      />
    </div>

    <div class="tw:flex tw:items-center tw:flex-1 tw:h-full">
      <input
        data-testid="select-search-input"
        ref="searchInput" 
        type="text" 
        placeholder="Search" 
        class="tw:w-full tw:h-full tw:px-3 tw:bg-transparent tw:outline-none" 
        v-model="queryInput" 
      />
      <button 
        v-if="queryInput.length > 0" 
        class="tw:px-3 tw:h-full tw:flex tw:items-center tw:justify-center"
        @click="handleClear"
      >
        <XIcon :size="15" class="tw:text-gray-500" />
      </button>
    </div>
  </div>
</template>
